// src/pages/Cadastrar.jsx - Envia payload compatível com o backend e gera título automático
import React, { useState, useEffect, useMemo } from 'react';
import { IMaskInput } from 'react-imask';
import { uploadImagem } from '../services/uploadImagem';
import { cadastrarImovel } from '../services/imoveisService';
import { criarNegocio } from '../services/negociosService'; // 🔹 Negócio automático (upgrade/permuta/downgrade/ponte)
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

import {
  Home,
  Building2,
  MapPinned,
  Trees,
  Tractor,
  Warehouse,
  Check,
} from 'lucide-react';

const Card = ({ title, children, className = '' }) => (
  <div
    className={`bg-white p-6 rounded-2xl shadow-lg border border-gray-100 ${className}`}
  >
    <h2 className="text-xl font-semibold text-gray-800 mb-4">{title}</h2>
    {children}
  </div>
);

const getInitials = (name) => {
  if (!name) return '';
  const p = name.trim().split(' ').filter(Boolean);
  const first = p[0]?.[0] || '';
  const last = p[p.length - 1]?.[0] || '';
  return (first + last).toUpperCase();
};

// Capitaliza por palavras (primeira letra maiúscula)
const capitalizeWords = (text = '') =>
  String(text)
    .trim()
    .replace(/\s+/g, ' ')
    .split(' ')
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1).toLowerCase() : ''))
    .join(' ');

// Converte string numérica (unmask do IMask) para number com centavos
const toNumberBRL = (val) => {
  if (val === null || val === undefined || val === '') return null;
  const n = Number(val);
  return Number.isFinite(n) ? n : null;
};

// Monta um título padrão quando o usuário não informar um
const montarTitulo = (f) => {
  const t = f.tipo || 'Imóvel';
  const b = f.bairro ? ` no ${f.bairro}` : '';
  const c = f.cidade ? `, ${f.cidade}` : '';
  return `${t}${b}${c}`.trim();
};

// valida se uma URL termina com extensão renderizável em navegador
const isRenderable = (u) =>
  /\.(jpe?g|png|webp|gif|avif)$/i.test((u || '').split('?')[0]);

/* ==========================
   ✅ RESUMO + BADGES
========================== */
const formatBRL0 = (n) =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  }).format(Number(n || 0));

const faixaLabel = (faixa) => {
  if (faixa === '30-50') return '30–50% (Saudável)';
  if (faixa === '50-90') return '50–90% (Venda rápida)';
  if (faixa === 'negociavel') return 'Permuta negociável';
  return '';
};

const buildPermutaResumo = (permuta) => {
  if (!permuta?.aceita) return '';
  const itensTxt = (permuta.itens || [])
    .map((i) => `${i.tipo} ${formatBRL0(i.valorAproximado)}`)
    .join(' + ');

  const faixaTxt = faixaLabel(permuta.faixa);
  return `Permuta: ${itensTxt}${faixaTxt ? ` | Faixa: ${faixaTxt}` : ''}`.trim();
};

const buildBadges = ({ movimentoImovel, permuta, ponteImobiliaria }) => {
  const b = [];

  if (movimentoImovel === 'upgrade')
    b.push({ key: 'upgrade', label: 'Upgrade' });

  if (movimentoImovel === 'downgrade')
    b.push({ key: 'downgrade', label: 'Downgrade' });

  if (permuta?.aceita) {
    b.push({ key: 'permuta', label: 'Aceita permuta' });

    const fx = permuta.faixa;
    if (fx === '30-50')
      b.push({ key: 'permuta_faixa', label: '30–50% (Saudável)' });

    if (fx === '50-90')
      b.push({ key: 'permuta_faixa', label: '50–90% (Venda rápida)' });

    if (fx === 'negociavel')
      b.push({ key: 'permuta_faixa', label: 'Permuta negociável' });
  }

  if (ponteImobiliaria?.ativa) {
    b.push({ key: 'ponte_imobiliaria', label: 'Ponte Imobiliária' });

    if (ponteImobiliaria?.cidadeOrigem && ponteImobiliaria?.cidadeDestino) {
      b.push({
        key: 'rota_ponte',
        label: `${ponteImobiliaria.cidadeOrigem} → ${ponteImobiliaria.cidadeDestino}`,
      });
    }

    b.push({ key: 'parceria_50_50', label: 'Parceria 50/50' });
  }

  return b;
};

const Cadastrar = () => {
  const { user, usuario } = useAuth();
  const navigate = useNavigate();

  const PERMUTA_OPTS = useMemo(
    () => [
      { key: 'casa', label: 'Casa', Icon: Home },
      { key: 'apartamento', label: 'Apartamento', Icon: Building2 },
      { key: 'terreno', label: 'Terreno', Icon: MapPinned },
      { key: 'chacara', label: 'Chácara', Icon: Trees },
      { key: 'fazenda', label: 'Fazenda', Icon: Tractor },
      { key: 'barracao', label: 'Barracão', Icon: Warehouse },
    ],
    []
  );

  const [form, setForm] = useState({
    refCodigo: '',
    titulo: '',
    tipo: '',
    status: 'publicado',
    statusNegocio: 'ativo',
    preco: '',

    estado: 'MT',
    cidade: '',
    bairro: '',
    endereco: '',
    numero: '',
    cep: '',

    dormitorios: '',
    banheiros: '',
    vagas: '',
    areaTerreno: '',
    areaConstruida: '',

    descricao: '',
    diferenciais: [],
    imagens: [],

    proprietarioNome: '',
    proprietarioTelefone: '',
    proprietarioTelefone2: '',

    // ✅ MOVIMENTO DO IMÓVEL
    // 'nenhum' | 'upgrade' | 'downgrade'
    movimentoImovel: 'nenhum',

    // ✅ PERMUTA
    aceitaPermuta: 'nao', // sim/nao
    permutaTipos: {
      casa: { ativo: false, valor: '' },
      apartamento: { ativo: false, valor: '' },
      terreno: { ativo: false, valor: '' },
      chacara: { ativo: false, valor: '' },
      fazenda: { ativo: false, valor: '' },
      barracao: { ativo: false, valor: '' },
    },
    percentualPermutaFaixa: '', // '30-50' | '50-90' | 'negociavel'

    // ✅ PONTE IMOBILIÁRIA
    ponteImobiliariaAtiva: 'nao', // sim/nao
    ponteEstadoDestino: 'MT',
    ponteCidadeDestino: '',
    ponteTipoDesejadoDestino: '',
    ponteCondicaoNegociacao: '', // sim | nao | a_negociar
    ponteObservacoes: '',

    // 🔒 CAMPOS ANTIGOS (compat)
    aceitaPermutaVeiculos: 'nao',
    aceitaPermutaImoveis: 'nao',
    permutaNegociavel: 'nao',
    percentualPermuta: '',

    financiamento: false,
    condominio: false,
    compartilharComSocios: false,
    compartilharComParceiros: false,

    temPlaca: false,

    // ✅ usado no Algolia/recência (15 dias)
    createdAtMs: null,
    createdAtClient: null,
  });

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (usuario?.nome && !form.refCodigo) {
      const initials = getInitials(usuario.nome);
      setForm((prev) => ({ ...prev, refCodigo: `${initials}001` }));
    }

    if (usuario && Array.isArray(usuario.socios) && usuario.socios.length > 0) {
      setForm((prev) => ({ ...prev, compartilharComSocios: true }));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usuario]);

  const diferenciaisLista = [
    'Condomínio fechado',
    'Piscina',
    'Churrasqueira',
    'Área Gourmet',
    'Closet',
    'Placa Solar',
    'Portaria 24h',
    'Piscina Aquecida',
    'Persianas Automáticas',
    'Iluminação LED',
    'Adega',
    'Armários Embutidos',
  ];

  const tiposImovel = [
    'Casa',
    'Apartamento',
    'Apartamento na planta',
    'Kitnet',
    'Terreno / Área',
    'Sala comercial',
    'Ponto comercial',
    'Galpão / Barracão',
    'Empreendimentos',
    'Chácara',
    'Fazenda',
  ];

  const tiposDestinoPonte = [
    'Casa',
    'Apartamento',
    'Apartamento na planta',
    'Kitnet',
    'Terreno / Área',
    'Sala comercial',
    'Ponto comercial',
    'Galpão / Barracão',
    'Empreendimentos',
    'Chácara',
    'Fazenda',
    'Indiferente',
  ];

  const estadosBrasil = [
    'AC',
    'AL',
    'AP',
    'AM',
    'BA',
    'CE',
    'DF',
    'ES',
    'GO',
    'MA',
    'MT',
    'MS',
    'MG',
    'PA',
    'PB',
    'PR',
    'PE',
    'PI',
    'RJ',
    'RN',
    'RS',
    'RO',
    'RR',
    'SC',
    'SP',
    'SE',
    'TO',
  ];

  const cidadesMT = [
    'Cuiabá',
    'Várzea Grande',
    'Rondonópolis',
    'Sinop',
    'Sorriso',
  ];

  const handleChange = (campo, valor) =>
    setForm((prev) => ({ ...prev, [campo]: valor }));

  const toggleDiferencial = (dif) => {
    setForm((prev) => ({
      ...prev,
      diferenciais: prev.diferenciais.includes(dif)
        ? prev.diferenciais.filter((d) => d !== dif)
        : [...prev.diferenciais, dif],
    }));
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files || []);

    if (files.length > 7)
      return toast.error('Você pode enviar no máximo 7 imagens.');

    const allowed = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    const invalid = files.find((f) => !allowed.includes(f.type));

    if (invalid) {
      return toast.error('Formato não suportado. Use JPG, PNG, WEBP ou GIF.');
    }

    setForm((prev) => ({ ...prev, imagens: files }));
  };

  const togglePermutaTipo = (key) => {
    setForm((prev) => {
      const ativoAtual = !!prev.permutaTipos?.[key]?.ativo;

      return {
        ...prev,
        permutaTipos: {
          ...prev.permutaTipos,
          [key]: {
            ...prev.permutaTipos[key],
            ativo: !ativoAtual,
            valor: ativoAtual ? '' : prev.permutaTipos[key]?.valor || '',
          },
        },
      };
    });
  };

  const setPermutaValor = (key, valor) => {
    setForm((prev) => ({
      ...prev,
      permutaTipos: {
        ...prev.permutaTipos,
        [key]: {
          ...prev.permutaTipos[key],
          valor,
        },
      },
    }));
  };

  const getPermutaItensPayload = () => {
    const itens = [];

    for (const opt of PERMUTA_OPTS) {
      const item = form.permutaTipos?.[opt.key];

      if (item?.ativo) {
        itens.push({
          tipo: opt.label,
          key: opt.key,
          valorAproximado: toNumberBRL(item.valor) || 0,
        });
      }
    }

    return itens;
  };

  const handlePonteAtivaChange = (valor) => {
    setForm((prev) => ({
      ...prev,
      ponteImobiliariaAtiva: valor,
      compartilharComParceiros:
        valor === 'sim' ? true : prev.compartilharComParceiros,
      ponteEstadoDestino:
        valor === 'sim'
          ? prev.ponteEstadoDestino || prev.estado || 'MT'
          : prev.ponteEstadoDestino,
      ponteCidadeDestino: valor === 'sim' ? prev.ponteCidadeDestino : '',
      ponteTipoDesejadoDestino:
        valor === 'sim' ? prev.ponteTipoDesejadoDestino : '',
      ponteCondicaoNegociacao:
        valor === 'sim' ? prev.ponteCondicaoNegociacao : '',
      ponteObservacoes: valor === 'sim' ? prev.ponteObservacoes : '',
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!user) return toast.error('Você precisa estar logado para cadastrar.');

    const corretorUid = user.uid;
    const corretorNome = usuario?.nome || user.displayName || 'Corretor';

    setLoading(true);

    try {
      const obrigatorios = [
        { campo: form.tipo, nome: 'Tipo de imóvel' },
        { campo: form.preco, nome: 'Preço' },
        { campo: form.cidade, nome: 'Cidade' },
        { campo: form.bairro, nome: 'Bairro' },
        { campo: form.descricao, nome: 'Descrição' },
        { campo: form.endereco, nome: 'Endereço' },
        { campo: form.numero, nome: 'Número' },
        { campo: form.cep, nome: 'CEP' },
        { campo: form.proprietarioNome, nome: 'Nome do proprietário' },
        { campo: form.proprietarioTelefone, nome: 'Telefone do proprietário' },
      ];

      for (const item of obrigatorios) {
        if (!item.campo || String(item.campo).trim() === '') {
          setLoading(false);
          return toast.error(`Preencha o campo obrigatório: ${item.nome}`);
        }
      }

      if (!form.imagens?.length) {
        setLoading(false);
        return toast.error('Envie pelo menos 1 imagem do imóvel.');
      }

      // ✅ validações permuta
      if (form.aceitaPermuta === 'sim') {
        const itens = getPermutaItensPayload();

        if (!itens.length) {
          setLoading(false);
          return toast.error(
            'Selecione pelo menos 1 tipo de permuta (Casa, Apartamento, etc.).'
          );
        }

        const faltandoValor = itens.find(
          (i) => !i.valorAproximado || i.valorAproximado <= 0
        );

        if (faltandoValor) {
          setLoading(false);
          return toast.error(
            'Informe o valor aproximado do bem de permuta selecionado.'
          );
        }

        if (!form.percentualPermutaFaixa) {
          setLoading(false);
          return toast.error('Selecione o percentual de permuta aceito.');
        }
      }

      // ✅ validações Ponte Imobiliária
      const isPonte = form.ponteImobiliariaAtiva === 'sim';

      if (isPonte) {
        if (!form.ponteEstadoDestino) {
          setLoading(false);
          return toast.error(
            'Informe o estado de destino da Ponte Imobiliária.'
          );
        }

        if (!form.ponteCidadeDestino?.trim()) {
          setLoading(false);
          return toast.error(
            'Informe a cidade de destino da Ponte Imobiliária.'
          );
        }

        if (!form.ponteTipoDesejadoDestino) {
          setLoading(false);
          return toast.error(
            'Informe o tipo de imóvel desejado na cidade de destino.'
          );
        }

        if (!form.ponteCondicaoNegociacao) {
          setLoading(false);
          return toast.error(
            'Informe a condição de negociação da Ponte Imobiliária.'
          );
        }
      }

      // Upload das imagens
      const imagensEnviadas = await Promise.all(
        form.imagens.map((file) => uploadImagem(file, user))
      );

      const imagensParaBackend = imagensEnviadas.map((img) => ({
        url: img.url,
        path: img.path,
      }));

      const capa =
        imagensParaBackend.find((i) => isRenderable(i.url)) ||
        imagensParaBackend[0];

      const thumbnail = capa?.url || '';

      const tituloFinal = (form.titulo || montarTitulo(form)).trim();

      // ✅ permuta estruturada
      const permutaItens =
        form.aceitaPermuta === 'sim' ? getPermutaItensPayload() : [];

      const permutaFaixa =
        form.aceitaPermuta === 'sim' ? form.percentualPermutaFaixa : '';

      const permutaNegociavelBool = permutaFaixa === 'negociavel';

      // compat antigos
      const compatAceitaPermutaImoveis =
        form.aceitaPermuta === 'sim' ? 'sim' : 'nao';

      const compatPercentualPermuta =
        permutaFaixa === '30-50'
          ? '50'
          : permutaFaixa === '50-90'
            ? '90'
            : 'negociavel';

      // ✅ flags
      const isUpgrade = form.movimentoImovel === 'upgrade';
      const isDowngrade = form.movimentoImovel === 'downgrade';
      const isPermuta = form.aceitaPermuta === 'sim';

      const permutaObj = {
        aceita: isPermuta,
        itens: permutaItens,
        faixa: permutaFaixa,
        negociavel: permutaNegociavelBool,
      };

      // ✅ Ponte Imobiliária estruturada
      const cidadeOrigem = capitalizeWords(form.cidade);
      const estadoOrigem = (form.estado || '').trim().toUpperCase();
      const cidadeDestino = capitalizeWords(form.ponteCidadeDestino);
      const estadoDestino = (form.ponteEstadoDestino || '')
        .trim()
        .toUpperCase();

      const ponteImobiliariaObj = {
        ativa: isPonte,
        cidadeOrigem: isPonte ? cidadeOrigem : '',
        estadoOrigem: isPonte ? estadoOrigem : '',
        cidadeDestino: isPonte ? cidadeDestino : '',
        estadoDestino: isPonte ? estadoDestino : '',
        tipoDesejadoDestino: isPonte ? form.ponteTipoDesejadoDestino : '',
        condicaoNegociacao: isPonte ? form.ponteCondicaoNegociacao : '',
        observacoes: isPonte ? String(form.ponteObservacoes || '').trim() : '',
        parceriaAutomatica: isPonte,
        regraComissao: isPonte ? '50-50' : '',
        status: isPonte ? 'ativa' : 'inativa',
      };

      const permutaResumo = buildPermutaResumo(permutaObj);

      const ponteResumo = isPonte
        ? `Ponte Imobiliária: ${cidadeOrigem}/${estadoOrigem} → ${cidadeDestino}/${estadoDestino}`
        : '';

      const badges = buildBadges({
        movimentoImovel: form.movimentoImovel,
        permuta: permutaObj,
        ponteImobiliaria: ponteImobiliariaObj,
      });

      // ✅ Recência (15 dias): carimbo numérico e ISO (debug)
      const nowMs = Date.now();

      const payload = {
        refCodigo: String(form.refCodigo || '').toUpperCase(),
        titulo: capitalizeWords(tituloFinal),
        tipo: form.tipo,

        status: form.status || 'publicado',
        statusNegocio: form.statusNegocio || 'ativo',

        estado: estadoOrigem,
        cidade: cidadeOrigem,
        bairro: capitalizeWords(form.bairro),

        preco: toNumberBRL(form.preco),

        dormitorios: parseInt(form.dormitorios, 10) || 0,
        suites: 0,
        banheiros: parseInt(form.banheiros, 10) || 0,
        vagas: parseInt(form.vagas, 10) || 0,

        areaTerreno: parseInt(form.areaTerreno, 10) || 0,
        areaConstruida: parseInt(form.areaConstruida, 10) || 0,

        rua: capitalizeWords(form.endereco),
        numero: String(form.numero || '').replace(/\D/g, ''),
        cep: String(form.cep || '').replace(/\D/g, ''),

        diferenciais: form.diferenciais,
        piscina: form.diferenciais.includes('Piscina'),
        churrasqueira: form.diferenciais.includes('Churrasqueira'),
        financiamento: !!form.financiamento,
        condominio: !!form.condominio,

        descricao: form.descricao,

        imagens: imagensParaBackend,
        thumbnail,

        proprietarioNome: capitalizeWords(form.proprietarioNome),
        proprietarioTelefone: String(form.proprietarioTelefone || '').trim(),
        proprietarioTelefone2: String(form.proprietarioTelefone2 || '').trim(),

        // ✅ movimento
        movimentoImovel: form.movimentoImovel,

        // ✅ compat legado
        aceleradorVendas: isUpgrade ? 'sim' : 'nao',

        // ✅ permuta
        permuta: permutaObj,
        permutaResumo,

        // ✅ Ponte Imobiliária
        ponteImobiliaria: ponteImobiliariaObj,
        ponteResumo,
        motivoVenda: isPonte ? 'migracao' : '',
        visibilidadeRede: isPonte ? 'ponte_imobiliaria' : '',
        regraComissao: isPonte ? '50-50' : '',

        // ✅ badges
        badges,

        // 🔒 compat antigos
        aceitaPermutaVeiculos: form.aceitaPermutaVeiculos,
        aceitaPermutaImoveis: compatAceitaPermutaImoveis,
        permutaNegociavel: permutaNegociavelBool,
        percentualPermuta: permutaNegociavelBool
          ? 0
          : parseInt(compatPercentualPermuta, 10) || 0,

        compartilharComSocios: !!form.compartilharComSocios,

        // ✅ se for Ponte, força parceria automática
        compartilharComParceiros: isPonte
          ? true
          : !!form.compartilharComParceiros,

        temPlaca: !!form.temPlaca,

        ownerId: corretorUid,
        ownerNome: corretorNome,
        criadoPor: {
          uid: corretorUid,
          nome: corretorNome,
          email: user.email || '',
        },

        // ✅ Contato profissional do corretor angariador
        // Usado na Ponte Imobiliária para permitir contato entre corretores
        // sem expor dados privados do proprietário.
        corretorContato: {
          uid: corretorUid,
          nome: corretorNome,
          email: user.email || '',
          telefone:
            usuario?.telefone || usuario?.whatsapp || usuario?.celular || '',
          creci: usuario?.creci || '',
          fotoUrl: usuario?.fotoPerfil?.url || '',
        },

        // ✅ campos para “imóvel recente (15 dias)”
        createdAtMs: nowMs,
        createdAtClient: new Date(nowMs).toISOString(),
      };

      const resultadoCadastro = await cadastrarImovel(payload);

      const imovelId =
        resultadoCadastro?.id ||
        resultadoCadastro?.imovelId ||
        resultadoCadastro?.docId ||
        resultadoCadastro?.data?.id ||
        null;

      if (!imovelId) {
        console.warn(
          '[Cadastrar] Não foi possível identificar o ID do imóvel recém-criado.',
          resultadoCadastro
        );
      }

      // ✅ Negócio automático: Upgrade / Downgrade / Permuta / Ponte Imobiliária
      if (
        (isUpgrade || isDowngrade || isPermuta || isPonte) &&
        imovelId &&
        corretorUid
      ) {
        const bairros =
          payload.bairro && typeof payload.bairro === 'string'
            ? [payload.bairro.trim()].filter(Boolean)
            : [];

        const nomeCliente =
          form.proprietarioNome?.trim() ||
          payload.proprietarioNome?.trim() ||
          'Proprietário do imóvel';

        const origem = isPonte
          ? 'ponte-imobiliaria'
          : isUpgrade && isPermuta
            ? 'upgrade-permuta-imovel'
            : isDowngrade && isPermuta
              ? 'downgrade-permuta-imovel'
              : isUpgrade
                ? 'upgrade-imovel'
                : isDowngrade
                  ? 'downgrade-imovel'
                  : 'permuta-imovel';

        const descricaoNegocio = isPonte
          ? `Ponte Imobiliária do imóvel ${payload.refCodigo || imovelId}: ${
              ponteImobiliariaObj.cidadeOrigem
            }/${ponteImobiliariaObj.estadoOrigem} → ${
              ponteImobiliariaObj.cidadeDestino
            }/${ponteImobiliariaObj.estadoDestino}`.trim()
          : isUpgrade
            ? `Upgrade do imóvel ${payload.refCodigo || imovelId} em ${
                payload.bairro || ''
              } - ${payload.cidade || ''}`.trim()
            : isDowngrade
              ? `Downgrade do imóvel ${payload.refCodigo || imovelId} em ${
                  payload.bairro || ''
                } - ${payload.cidade || ''}`.trim()
              : `Permuta do imóvel ${payload.refCodigo || imovelId} em ${
                  payload.bairro || ''
                } - ${payload.cidade || ''}`.trim();

        try {
          await criarNegocio({
            usuario: { uid: corretorUid, nome: corretorNome },
            dados: {
              origem,
              tipoNegocio: isPonte ? 'migracao' : 'imovel',
              imovelOrigemId: imovelId,
              upgradeImovelId: isUpgrade ? imovelId : null,
              downgradeImovelId: isDowngrade ? imovelId : null,
              permutaImovelId: isPermuta ? imovelId : null,
              ponteImobiliariaId: isPonte ? imovelId : null,

              // ✅ ajuda a engine
              movimento: isPonte ? 'migracao' : form.movimentoImovel,

              nomeCliente: capitalizeWords(nomeCliente),
              clientePrimeiroNome:
                capitalizeWords(nomeCliente).split(' ')[0] ||
                capitalizeWords(nomeCliente),

              tipoImovel: payload.tipo,
              cidade: payload.cidade,
              cidadePrincipal: payload.cidade,

              bairros,
              bairrosDesejados: bairros,

              valorMin: payload.preco,
              valorMax: payload.preco,

              // ✅ extras p/ Máquina de Vendas
              permuta: payload.permuta || null,
              permutaResumo: payload.permutaResumo || '',
              badges: payload.badges || [],

              // ✅ extras Ponte Imobiliária
              ponteImobiliaria: payload.ponteImobiliaria || null,
              ponteResumo: payload.ponteResumo || '',
              cidadeOrigem: ponteImobiliariaObj.cidadeOrigem || '',
              estadoOrigem: ponteImobiliariaObj.estadoOrigem || '',
              cidadeDestino: ponteImobiliariaObj.cidadeDestino || '',
              estadoDestino: ponteImobiliariaObj.estadoDestino || '',
              tipoDesejadoDestino:
                ponteImobiliariaObj.tipoDesejadoDestino || '',
              condicaoNegociacao: ponteImobiliariaObj.condicaoNegociacao || '',
              regraComissao: isPonte ? '50-50' : '',

              descricao: descricaoNegocio,

              // ✅ Ponte entra como oportunidade de rede
              statusPipeline: isPonte ? 'match_pendente' : 'contato',
              visibilidade: isPonte ? 'ponte_imobiliaria' : 'privado',
            },
          });
        } catch (err) {
          console.error(
            'Erro ao criar negócio automático (movimento/permuta/ponte):',
            err
          );
        }
      }

      toast.success('Imóvel cadastrado com sucesso!');

      setForm((prev) => ({
        ...prev,
        refCodigo: '',
        titulo: '',
        tipo: '',
        status: 'publicado',
        statusNegocio: 'ativo',
        preco: '',

        estado: 'MT',
        cidade: '',
        bairro: '',
        endereco: '',
        numero: '',
        cep: '',

        dormitorios: '',
        banheiros: '',
        vagas: '',
        areaTerreno: '',
        areaConstruida: '',

        descricao: '',
        diferenciais: [],
        imagens: [],

        proprietarioNome: '',
        proprietarioTelefone: '',
        proprietarioTelefone2: '',

        // ✅ reset do movimento
        movimentoImovel: 'nenhum',

        aceitaPermuta: 'nao',
        permutaTipos: {
          casa: { ativo: false, valor: '' },
          apartamento: { ativo: false, valor: '' },
          terreno: { ativo: false, valor: '' },
          chacara: { ativo: false, valor: '' },
          fazenda: { ativo: false, valor: '' },
          barracao: { ativo: false, valor: '' },
        },
        percentualPermutaFaixa: '',

        // ✅ reset Ponte Imobiliária
        ponteImobiliariaAtiva: 'nao',
        ponteEstadoDestino: 'MT',
        ponteCidadeDestino: '',
        ponteTipoDesejadoDestino: '',
        ponteCondicaoNegociacao: '',
        ponteObservacoes: '',

        aceitaPermutaVeiculos: 'nao',
        aceitaPermutaImoveis: 'nao',
        permutaNegociavel: 'nao',
        percentualPermuta: '',

        financiamento: false,
        condominio: false,
        compartilharComSocios:
          Array.isArray(usuario?.socios) && usuario.socios.length > 0,
        compartilharComParceiros: false,
        temPlaca: false,

        // ✅ recência
        createdAtMs: null,
        createdAtClient: null,
      }));

      navigate('/perfil');
    } catch (error) {
      console.error(
        'Erro ao cadastrar imóvel:',
        error?.response?.status,
        error?.response?.data || error
      );

      toast.error(
        'Erro ao cadastrar imóvel. Verifique os campos e tente novamente.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 max-w-6xl mx-auto min-h-screen">
      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Imagens */}
        <Card title="Imagens do Imóvel">
          <label className="block text-sm font-medium mb-2 text-gray-700">
            Envie até 7 imagens (a primeira será a capa)
          </label>

          <input
            type="file"
            multiple
            accept="image/jpeg,image/png,image/webp,image/gif"
            onChange={handleImageChange}
            className="w-full text-gray-700"
          />

          {form.imagens.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2">
              {form.imagens.map((file, i) => (
                <span key={i} className="bg-gray-100 text-sm p-2 rounded-lg">
                  {file.name}
                </span>
              ))}
            </div>
          )}
        </Card>

        {/* Básicas */}
        <Card title="Informações Básicas">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="label">Código de Referência</label>
              <input
                type="text"
                placeholder="Ex: TC001"
                value={form.refCodigo}
                onChange={(e) => handleChange('refCodigo', e.target.value)}
                className="input"
                maxLength={10}
              />
            </div>

            <div>
              <label className="label">Tipo</label>
              <select
                value={form.tipo}
                onChange={(e) => handleChange('tipo', e.target.value)}
                className="input"
              >
                <option value="" disabled>
                  Selecione o tipo
                </option>
                {tiposImovel.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="label">Preço</label>
              <IMaskInput
                mask="R$ num"
                blocks={{
                  num: {
                    mask: Number,
                    thousandsSeparator: '.',
                    radix: ',',
                    scale: 2,
                    mapToRadix: [','],
                    padFractionalZeros: true,
                    normalizeZeros: true,
                    signed: false,
                  },
                }}
                unmask={true}
                placeholder="R$ 0,00"
                value={form.preco}
                onAccept={(v) => handleChange('preco', v)}
                className="input"
                inputMode="numeric"
              />
            </div>
          </div>
        </Card>

        {/* Localização */}
        <Card title="Localização">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="label">Estado</label>
              <select
                value={form.estado}
                onChange={(e) => handleChange('estado', e.target.value)}
                className="input"
              >
                <option value="" disabled>
                  Estado
                </option>
                {estadosBrasil.map((uf) => (
                  <option key={uf} value={uf}>
                    {uf}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="label">Cidade</label>
              <select
                value={form.cidade}
                onChange={(e) => handleChange('cidade', e.target.value)}
                className="input"
              >
                <option value="" disabled>
                  Selecione a cidade
                </option>
                {cidadesMT.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="label">Bairro</label>
              <input
                type="text"
                placeholder="Bairro"
                value={form.bairro}
                onChange={(e) => handleChange('bairro', e.target.value)}
                onBlur={() =>
                  handleChange('bairro', capitalizeWords(form.bairro))
                }
                className="input"
              />
            </div>
          </div>

          {user && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
              <div>
                <label className="label">Endereço</label>
                <input
                  type="text"
                  placeholder="Ex: Rua das Flores, Av. Brasil, Travessa X..."
                  value={form.endereco}
                  onChange={(e) => handleChange('endereco', e.target.value)}
                  onBlur={() =>
                    handleChange('endereco', capitalizeWords(form.endereco))
                  }
                  className="input"
                />
              </div>

              <div>
                <label className="label">Número</label>
                <IMaskInput
                  mask="num"
                  blocks={{
                    num: {
                      mask: Number,
                      thousandsSeparator: '',
                      signed: false,
                    },
                  }}
                  unmask={true}
                  placeholder="Número"
                  value={form.numero}
                  onAccept={(v) => handleChange('numero', v)}
                  className="input"
                  inputMode="numeric"
                />
              </div>

              <div>
                <label className="label">CEP</label>
                <IMaskInput
                  mask="00000-000"
                  unmask={true}
                  placeholder="CEP"
                  value={form.cep}
                  onAccept={(v) => handleChange('cep', v)}
                  className="input"
                  inputMode="numeric"
                />
              </div>
            </div>
          )}
        </Card>

        {/* Características */}
        <Card title="Características">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {['dormitorios', 'banheiros', 'vagas'].map((campo) => (
              <div key={campo}>
                <label className="label">
                  {campo.charAt(0).toUpperCase() + campo.slice(1)}
                </label>

                <select
                  value={form[campo]}
                  onChange={(e) => handleChange(campo, e.target.value)}
                  className="input"
                >
                  <option value="">Selecionar</option>
                  {[1, 2, 3, 4, 5].map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                  <option value="5+">5+</option>
                </select>
              </div>
            ))}

            <div>
              <label className="label">Área do terreno (m²)</label>
              <IMaskInput
                mask="num"
                blocks={{ num: { mask: Number, signed: false } }}
                unmask={true}
                placeholder="Ex: 360"
                value={form.areaTerreno}
                onAccept={(v) => handleChange('areaTerreno', v)}
                className="input"
                inputMode="numeric"
              />
            </div>

            <div>
              <label className="label">Área construída (m²)</label>
              <IMaskInput
                mask="num"
                blocks={{ num: { mask: Number, signed: false } }}
                unmask={true}
                placeholder="Ex: 180"
                value={form.areaConstruida}
                onAccept={(v) => handleChange('areaConstruida', v)}
                className="input"
                inputMode="numeric"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={form.financiamento}
                onChange={(e) =>
                  handleChange('financiamento', e.target.checked)
                }
              />
              <span className="text-sm text-gray-700">
                Aceita financiamento
              </span>
            </label>

            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={form.condominio}
                onChange={(e) => handleChange('condominio', e.target.checked)}
              />
              <span className="text-sm text-gray-700">Em condomínio</span>
            </label>

            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={form.temPlaca}
                onChange={(e) => handleChange('temPlaca', e.target.checked)}
              />
              <span className="text-sm text-gray-700">
                Imóvel com placa instalada
              </span>
            </label>
          </div>
        </Card>

        {/* Descrição */}
        <Card title="Descrição do Imóvel">
          <label className="label">Descrição (Obrigatório)</label>
          <textarea
            rows={8}
            placeholder="Descreva os detalhes do imóvel..."
            value={form.descricao}
            onChange={(e) => handleChange('descricao', e.target.value)}
            className="input resize-y"
          />
        </Card>

        {/* Proprietário */}
        {user && (
          <Card title="Informações do Proprietário">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="label">Nome do proprietário</label>
                <input
                  type="text"
                  placeholder="Nome do proprietário"
                  value={form.proprietarioNome}
                  onChange={(e) =>
                    handleChange('proprietarioNome', e.target.value)
                  }
                  onBlur={() =>
                    handleChange(
                      'proprietarioNome',
                      capitalizeWords(form.proprietarioNome)
                    )
                  }
                  className="input"
                />
              </div>

              <div>
                <label className="label">Telefone Principal</label>
                <IMaskInput
                  mask={[
                    { mask: '(00) 00000-0000' },
                    { mask: '(00) 0000-0000' },
                  ]}
                  unmask={true}
                  placeholder="(66) 99999-9999"
                  value={form.proprietarioTelefone}
                  onAccept={(v) => handleChange('proprietarioTelefone', v)}
                  className="input"
                  inputMode="tel"
                />
              </div>

              <div>
                <label className="label">Telefone Secundário (Opcional)</label>
                <IMaskInput
                  mask={[
                    { mask: '(00) 00000-0000' },
                    { mask: '(00) 0000-0000' },
                  ]}
                  unmask={true}
                  placeholder="(66) 9999-9999"
                  value={form.proprietarioTelefone2}
                  onAccept={(v) => handleChange('proprietarioTelefone2', v)}
                  className="input"
                  inputMode="tel"
                />
              </div>
            </div>
          </Card>
        )}

        {/* Diferenciais */}
        <Card title="Diferenciais">
          <div className="flex flex-wrap gap-2">
            {diferenciaisLista.map((dif) => (
              <button
                type="button"
                key={dif}
                onClick={() => toggleDiferencial(dif)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200 ${
                  form.diferenciais.includes(dif)
                    ? 'bg-green-600 text-white'
                    : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                }`}
              >
                {dif}
              </button>
            ))}
          </div>

          <p className="text-xs text-gray-500 mt-2">
            Obs.: Se marcar <b>Piscina</b> ou <b>Churrasqueira</b> aqui, elas
            também serão salvas como flags individuais para o filtro.
          </p>
        </Card>

        {/* Acelerador de Vendas */}
        <Card title="Acelerador de Vendas">
          <div className="space-y-6">
            {/* Pergunta 1 — Upgrade */}
            <div className="flex flex-col md:flex-row md:items-center gap-4">
              <label className="text-sm font-medium text-gray-700 w-full md:w-1/2">
                Este cliente deseja um imóvel de maior valor? (Upgrade)
              </label>

              <div className="flex gap-4">
                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    name="movimento_upgrade"
                    value="sim"
                    checked={form.movimentoImovel === 'upgrade'}
                    onChange={() => handleChange('movimentoImovel', 'upgrade')}
                  />
                  <span>Sim</span>
                </label>

                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    name="movimento_upgrade"
                    value="nao"
                    checked={form.movimentoImovel !== 'upgrade'}
                    onChange={() => {
                      if (form.movimentoImovel === 'upgrade') {
                        handleChange('movimentoImovel', 'nenhum');
                      }
                    }}
                  />
                  <span>Não</span>
                </label>
              </div>
            </div>

            {/* Pergunta 2 — Downgrade */}
            <div className="flex flex-col md:flex-row md:items-center gap-4">
              <label className="text-sm font-medium text-gray-700 w-full md:w-1/2">
                Este cliente deseja um imóvel de menor valor? (Downgrade)
              </label>

              <div className="flex gap-4">
                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    name="movimento_downgrade"
                    value="sim"
                    checked={form.movimentoImovel === 'downgrade'}
                    onChange={() =>
                      handleChange('movimentoImovel', 'downgrade')
                    }
                  />
                  <span>Sim</span>
                </label>

                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    name="movimento_downgrade"
                    value="nao"
                    checked={form.movimentoImovel !== 'downgrade'}
                    onChange={() => {
                      if (form.movimentoImovel === 'downgrade') {
                        handleChange('movimentoImovel', 'nenhum');
                      }
                    }}
                  />
                  <span>Não</span>
                </label>
              </div>
            </div>

            {/* Aceita permuta */}
            <div className="flex flex-col md:flex-row md:items-center gap-4">
              <label className="text-sm font-medium text-gray-700 w-full md:w-1/2">
                Este cliente aceita permuta como parte de pagamento?
              </label>

              <div className="flex gap-4">
                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    name="aceitaPermuta"
                    value="sim"
                    checked={form.aceitaPermuta === 'sim'}
                    onChange={(e) => {
                      const v = e.target.value;
                      handleChange('aceitaPermuta', v);
                      handleChange(
                        'aceitaPermutaImoveis',
                        v === 'sim' ? 'sim' : 'nao'
                      );

                      if (v !== 'sim') {
                        handleChange('percentualPermutaFaixa', '');

                        setForm((prev) => ({
                          ...prev,
                          permutaTipos: {
                            casa: { ativo: false, valor: '' },
                            apartamento: { ativo: false, valor: '' },
                            terreno: { ativo: false, valor: '' },
                            chacara: { ativo: false, valor: '' },
                            fazenda: { ativo: false, valor: '' },
                            barracao: { ativo: false, valor: '' },
                          },
                        }));
                      }
                    }}
                  />
                  <span>Sim</span>
                </label>

                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    name="aceitaPermuta"
                    value="nao"
                    checked={form.aceitaPermuta === 'nao'}
                    onChange={(e) => {
                      const v = e.target.value;
                      handleChange('aceitaPermuta', v);
                      handleChange('aceitaPermutaImoveis', 'nao');
                      handleChange('permutaNegociavel', 'nao');
                      handleChange('percentualPermuta', '');
                      handleChange('percentualPermutaFaixa', '');

                      setForm((prev) => ({
                        ...prev,
                        permutaTipos: {
                          casa: { ativo: false, valor: '' },
                          apartamento: { ativo: false, valor: '' },
                          terreno: { ativo: false, valor: '' },
                          chacara: { ativo: false, valor: '' },
                          fazenda: { ativo: false, valor: '' },
                          barracao: { ativo: false, valor: '' },
                        },
                      }));
                    }}
                  />
                  <span>Não</span>
                </label>
              </div>
            </div>

            {/* Tipos de permuta */}
            {form.aceitaPermuta === 'sim' && (
              <div className="space-y-4">
                <div>
                  <label className="label">
                    Tipos de permuta que o cliente aceita
                  </label>

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {PERMUTA_OPTS.map(({ key, label, Icon }) => {
                      const ativo = !!form.permutaTipos?.[key]?.ativo;

                      return (
                        <button
                          key={key}
                          type="button"
                          onClick={() => togglePermutaTipo(key)}
                          className={`permuta-tile ${ativo ? 'permuta-tile--on' : ''}`}
                        >
                          <div className="flex items-center gap-3">
                            <span
                              className={`permuta-icon ${ativo ? 'permuta-icon--on' : ''}`}
                            >
                              <Icon size={18} />
                            </span>

                            <div className="flex-1 text-left">
                              <div className="text-sm font-semibold text-gray-900">
                                {label}
                              </div>

                              <div className="text-xs text-gray-500">
                                {ativo
                                  ? 'Selecionado'
                                  : 'Clique para selecionar'}
                              </div>
                            </div>

                            {ativo && (
                              <span className="permuta-check">
                                <Check size={16} />
                              </span>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Valores aproximados */}
                <div>
                  <label className="label">
                    Valor aproximado do bem (por tipo selecionado)
                  </label>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {PERMUTA_OPTS.filter(
                      (o) => form.permutaTipos?.[o.key]?.ativo
                    ).map(({ key, label, Icon }) => (
                      <div key={key} className="permuta-value">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="permuta-icon permuta-icon--on">
                            <Icon size={16} />
                          </span>

                          <span className="text-sm font-medium text-gray-700">
                            {label}
                          </span>
                        </div>

                        <IMaskInput
                          mask="R$ num"
                          blocks={{
                            num: {
                              mask: Number,
                              thousandsSeparator: '.',
                              radix: ',',
                              scale: 2,
                              mapToRadix: [','],
                              padFractionalZeros: true,
                              normalizeZeros: true,
                              signed: false,
                            },
                          }}
                          unmask={true}
                          placeholder="R$ 0,00"
                          value={form.permutaTipos?.[key]?.valor || ''}
                          onAccept={(v) => setPermutaValor(key, v)}
                          className="input"
                          inputMode="numeric"
                        />
                      </div>
                    ))}

                    {!PERMUTA_OPTS.some(
                      (o) => form.permutaTipos?.[o.key]?.ativo
                    ) && (
                      <div className="text-sm text-gray-500">
                        Selecione pelo menos um tipo de permuta acima para
                        informar o valor.
                      </div>
                    )}
                  </div>
                </div>

                {/* Percentual */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="label">
                      Qual percentual de permuta aceita?
                    </label>

                    <select
                      value={form.percentualPermutaFaixa}
                      onChange={(e) => {
                        const v = e.target.value;
                        handleChange('percentualPermutaFaixa', v);

                        if (v === 'negociavel') {
                          handleChange('permutaNegociavel', 'sim');
                          handleChange('percentualPermuta', 'negociavel');
                        } else {
                          handleChange('permutaNegociavel', 'nao');
                          handleChange(
                            'percentualPermuta',
                            v === '30-50' ? '50' : '90'
                          );
                        }
                      }}
                      className="input"
                    >
                      <option value="" disabled>
                        Selecione
                      </option>
                      <option value="30-50">30–50% — Saudável</option>
                      <option value="50-90">50–90% — Venda rápida</option>
                      <option value="negociavel">
                        Permuta negociável — aberto a negociação
                      </option>
                    </select>
                  </div>

                  <div className="text-xs text-gray-500 flex items-center">
                    Essa informação melhora o match da Máquina de Vendas e evita
                    propostas fora do padrão do proprietário.
                  </div>
                </div>
              </div>
            )}
          </div>
        </Card>

        {/* Ponte Imobiliária */}
        <Card title="Ponte Imobiliária">
          <div className="space-y-6">
            <div className="rounded-2xl border border-blue-100 bg-blue-50/60 p-4">
              <div className="flex items-start gap-3">
                <span className="mt-0.5 h-10 w-10 rounded-xl bg-blue-600 text-white flex items-center justify-center">
                  <MapPinned size={20} />
                </span>

                <div>
                  <h3 className="text-sm font-bold text-gray-900">
                    Conecte mudanças de cidade a oportunidades de parceria
                  </h3>

                  <p className="text-sm text-gray-600 mt-1">
                    A Ponte Imobiliária ajuda a conectar proprietários que estão
                    mudando de cidade com corretores que possuem opções no
                    destino.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex flex-col md:flex-row md:items-center gap-4">
              <label className="text-sm font-medium text-gray-700 w-full md:w-1/2">
                O proprietário deste imóvel está mudando de cidade?
              </label>

              <div className="flex gap-4">
                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    name="ponteImobiliariaAtiva"
                    value="sim"
                    checked={form.ponteImobiliariaAtiva === 'sim'}
                    onChange={(e) => handlePonteAtivaChange(e.target.value)}
                  />
                  <span>Sim</span>
                </label>

                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    name="ponteImobiliariaAtiva"
                    value="nao"
                    checked={form.ponteImobiliariaAtiva === 'nao'}
                    onChange={(e) => handlePonteAtivaChange(e.target.value)}
                  />
                  <span>Não</span>
                </label>
              </div>
            </div>

            {form.ponteImobiliariaAtiva === 'sim' && (
              <div className="space-y-5">
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                  <strong>Atenção:</strong> Todo imóvel marcado como Ponte
                  Imobiliária entra automaticamente como oportunidade de
                  parceria entre corretores. A plataforma mostra possibilidades;
                  os corretores conduzem a negociação.
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="label">Cidade de origem</label>
                    <input
                      type="text"
                      value={form.cidade || ''}
                      readOnly
                      className="input bg-gray-50 text-gray-500"
                      placeholder="Preenchida pela cidade do imóvel"
                    />
                    <p className="text-xs text-gray-400 mt-1">
                      A origem é a cidade onde o imóvel está localizado.
                    </p>
                  </div>

                  <div>
                    <label className="label">Estado de origem</label>
                    <input
                      type="text"
                      value={form.estado || ''}
                      readOnly
                      className="input bg-gray-50 text-gray-500"
                      placeholder="Preenchido pelo estado do imóvel"
                    />
                  </div>

                  <div>
                    <label className="label">Estado de destino</label>
                    <select
                      value={form.ponteEstadoDestino}
                      onChange={(e) =>
                        handleChange('ponteEstadoDestino', e.target.value)
                      }
                      className="input"
                    >
                      <option value="" disabled>
                        Selecione o estado
                      </option>
                      {estadosBrasil.map((uf) => (
                        <option key={uf} value={uf}>
                          {uf}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="label">Cidade de destino</label>
                    <input
                      type="text"
                      placeholder="Ex: Cuiabá, São Paulo, Sorriso..."
                      value={form.ponteCidadeDestino}
                      onChange={(e) =>
                        handleChange('ponteCidadeDestino', e.target.value)
                      }
                      onBlur={() =>
                        handleChange(
                          'ponteCidadeDestino',
                          capitalizeWords(form.ponteCidadeDestino)
                        )
                      }
                      className="input"
                    />
                  </div>

                  <div>
                    <label className="label">
                      Tipo de imóvel desejado no destino
                    </label>
                    <select
                      value={form.ponteTipoDesejadoDestino}
                      onChange={(e) =>
                        handleChange('ponteTipoDesejadoDestino', e.target.value)
                      }
                      className="input"
                    >
                      <option value="" disabled>
                        Selecione
                      </option>
                      {tiposDestinoPonte.map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="label">
                      O proprietário aceita avaliar imóveis no destino?
                    </label>
                    <select
                      value={form.ponteCondicaoNegociacao}
                      onChange={(e) =>
                        handleChange('ponteCondicaoNegociacao', e.target.value)
                      }
                      className="input"
                    >
                      <option value="" disabled>
                        Selecione
                      </option>
                      <option value="sim">Sim</option>
                      <option value="nao">Não</option>
                      <option value="a_negociar">A negociar</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="label">Observações da Ponte</label>
                  <textarea
                    rows={4}
                    placeholder="Ex: proprietário está indo para Cuiabá e deseja avaliar apartamento ou casa em condomínio."
                    value={form.ponteObservacoes}
                    onChange={(e) =>
                      handleChange('ponteObservacoes', e.target.value)
                    }
                    className="input resize-y"
                  />
                </div>
              </div>
            )}
          </div>
        </Card>

        <div>
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-6 rounded-xl transition-colors duration-200"
          >
            {loading ? 'Salvando...' : 'Cadastrar Imóvel'}
          </button>
        </div>
      </form>

      <style jsx>{`
        .input {
          @apply block w-full p-3 border border-gray-300 rounded-lg text-base text-gray-900 focus:ring-green-500 focus:border-green-500;
        }
        .label {
          @apply block text-sm font-medium text-gray-500 mb-2;
        }
        .permuta-tile {
          @apply w-full p-4 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 transition-colors;
        }
        .permuta-tile--on {
          @apply border-green-300 bg-green-50;
        }
        .permuta-icon {
          @apply h-9 w-9 rounded-lg bg-gray-100 text-gray-600 flex items-center justify-center;
        }
        .permuta-icon--on {
          @apply bg-green-600 text-white;
        }
        .permuta-check {
          @apply h-7 w-7 rounded-full bg-green-600 text-white flex items-center justify-center;
        }
        .permuta-value {
          @apply p-4 rounded-xl border border-gray-200 bg-white;
        }
      `}</style>
    </div>
  );
};

export default Cadastrar;
